// Type-only: keeps the store out of this module's runtime import graph.
import type { RootState } from "../../redux/store";
import { identifyUXCamUser, startUXCam } from "../../utils/UXCam";

/** The slice of the redux store this needs — keeps it trivially fake-able in tests. */
export interface UXCamSessionStore {
    getState: () => RootState;
    subscribe: (listener: () => void) => unknown;
}

/**
 * Owns the UXCam session for the whole app lifetime. Called once from AppInit.
 *
 * REFSIX-776 originally started recording from `AuthStackScreen`, which is only
 * mounted while the user is logged out — so an already-authenticated user
 * relaunching the app was never recorded at all. Starting here instead means
 * every app open is captured, whatever the auth state, which is also the
 * literal reading of the AC ("GIVEN app is opened ... THEN start recording").
 *
 * The identity is then attached from the store rather than from the
 * register/login call sites, so it covers the cases those miss: a cold start
 * straight into an authenticated app, and a re-login after logout (the logout
 * flow calls resetUXCamSession, so the next account lands on its own session
 * rather than being stitched onto the previous user's replay).
 *
 * Both UXCam calls no-op when the native plugin is absent (web / Cypress).
 */
export function initUXCamSession(store: UXCamSessionStore): void {
    startUXCam();

    let identified: string | undefined;
    const syncIdentity = () => {
        const userId = store.getState().auth?.session?.user_id;
        if (!userId) {
            // Logged out: forget the identity so the same account logging back
            // in is re-identified against its new session.
            identified = undefined;
            return;
        }
        if (userId === identified) return;
        identified = userId;
        identifyUXCamUser(userId);
    };
    // Handle the state that exists NOW, not just future changes — the same
    // shape as MixpanelUserId's constructor. Subscribing alone only works
    // while nothing dispatches before this runs; if AppInit ever gains an
    // await ahead of it, a cold start into an authenticated app would record
    // an anonymous session.
    syncIdentity();
    store.subscribe(syncIdentity);
}
